/**
 * Price updates (#1383, `docs/scope.md` §0 "Decision (Clayton, 2026-11-02, issue #1383)"): the licence lines whose
 * Pax8 price changes in a month, with cost, MSRP, sell price and margin before and after, the proposed sell price
 * (setting `price_rule`), a what-if price that shows the margin live, and Approve / Override / Skip.
 *
 * Nothing is written until a person confirms. Approve sends `rate: null` (the proposal), Override sends the typed
 * what-if price; both send the proposal's `computed_at`, and a 409 means the numbers were recomputed: the panel reads
 * again and says so. With `books_write_enabled` off the API hands the change to billing instead of writing Books.
 * Approve also drafts the customer notice; it is shown here to copy and is never sent from this screen.
 *
 * All prices are per user per month, like the rest of the Licences screen. A withheld or missing figure is a dash,
 * never $0 (hard rules 5 and 10).
 */
import { Button, Chip, cn, Dialog, EmptyState, ErrorBanner, Textarea, toast, useIsSinglePane } from '@patchbay/ui'
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { ApiError, describeError, unwrap } from '../../lib/api/client'
import {
  approveLicencePriceApiV1LicencesPriceUpdatesProposalIdApprovePost,
  licencePriceUpdatesApiV1LicencesPriceUpdatesGet,
  type MarginBand,
  type PriceProposalOut,
  type PriceUpdatesOut,
  type ProposalState,
  skipLicencePriceApiV1LicencesPriceUpdatesProposalIdSkipPost,
} from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { type MoneyValue, Tile, timeLabel } from '../profit/format'
import { QueryView } from '../profit/states'
import {
  bandOf,
  cad,
  cadCompact,
  DASH_CELL,
  dayLabel,
  FilterChips,
  type GroupedColumn,
  GroupedTable,
  MARGIN_TONE,
  MarginValue,
  marginBand,
  marginPct,
  num,
  pct1,
  qty,
  signed,
} from './common'

// --- query -------------------------------------------------------------------------------------------------------------

/** Under `['licences']`, so a licence write that invalidates the screen also re-reads the price updates. */
export const priceUpdatesKey = (month: string | null) => ['licences', 'price-updates', month ?? 'default'] as const

/** `month` is `YYYY-MM`; null asks the API for its default month. */
export const priceUpdatesQuery = (month: string | null) =>
  queryOptions({
    queryKey: priceUpdatesKey(month),
    queryFn: () => unwrap<PriceUpdatesOut>(licencePriceUpdatesApiV1LicencesPriceUpdatesGet(month ? { query: { month } } : undefined)),
  })

// --- what-if maths (pure, client side; nothing is written) ------------------------------------------------------------

/** A typed price: dollars per user per month, rounded to the cent. Null when it is not a usable price. */
export function parsePrice(text: string): number | null {
  const t = text.replace(/[$,\s]/g, '')
  if (!t || !/^\d*\.?\d*$/.test(t)) return null
  const n = Number(t)
  return Number.isFinite(n) && n > 0 && n <= 100000 ? Math.round(n * 100) / 100 : null
}

/** Money as a what-if field's starting text ("17.5" → "17.50"); empty when absent. */
export const priceText = (value: MoneyValue): string => {
  const n = num(value)
  return n === null ? '' : n.toFixed(2)
}

export interface WhatIf {
  price: number
  /** Margin on the price at the given cost; null when the cost is unknown or withheld. */
  margin: number | null
  band: MarginBand
  /** (price − sell now) × quantity: what this row bills a month more (or less) than today. Null when a side is unknown. */
  monthly: number | null
  belowCost: boolean
}

export function whatIf(price: number | null, opts: { cost: MoneyValue; sellNow: MoneyValue; quantity: MoneyValue }): WhatIf | null {
  if (price === null) return null
  const cost = num(opts.cost)
  const now = num(opts.sellNow)
  const n = num(opts.quantity)
  const margin = marginPct(price, cost)
  return {
    price,
    margin,
    band: marginBand(margin),
    monthly: now === null || n === null ? null : (price - now) * n,
    belowCost: cost !== null && price < cost,
  }
}

const same = (a: number | null, b: number | null) => a !== null && b !== null && Math.abs(a - b) < 0.005

// --- small pieces shared with the Renewals list ------------------------------------------------------------------------

/**
 * A subtle signed change under a value. `cost`: up is red, down green (it is our cost). Anything else stays muted.
 * Nothing when either side is unknown or there is no change.
 */
export function Delta({ before, after, tone = 'neutral' }: { before: MoneyValue; after: MoneyValue; tone?: 'cost' | 'neutral' }) {
  const a = num(before)
  const b = num(after)
  if (a === null || b === null || same(a, b)) return null
  const d = b - a
  return (
    <span
      data-testid="delta"
      className={cn('block text-2xs', tone === 'cost' ? (d > 0 ? 'text-destructive' : 'text-success') : 'text-muted-foreground')}
    >
      {signed(d, cad)}
    </span>
  )
}

/** A money value with its change from `before` underneath. */
export function MoneyWithDelta({ value, before, tone }: { value: MoneyValue; before?: MoneyValue; tone?: 'cost' | 'neutral' }) {
  if (num(value) === null) return DASH_CELL
  return (
    <span>
      {cad(value)}
      {before !== undefined ? <Delta before={before} after={value} tone={tone} /> : null}
    </span>
  )
}

/**
 * The what-if price: type a price per user a month and see the margin at the cost after the change (in its band
 * colour) and what the row bills a month more or less than today. Controlled; nothing is saved.
 */
export function WhatIfField({
  label,
  value,
  onChange,
  cost,
  sellNow,
  quantity,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  cost: MoneyValue
  sellNow: MoneyValue
  quantity: MoneyValue
}) {
  const price = parsePrice(value)
  const w = whatIf(price, { cost, sellNow, quantity })
  const invalid = value.trim() !== '' && price === null
  return (
    <div className="flex min-w-32 flex-col items-end gap-0.5 font-sans" data-testid="what-if">
      <span className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground" aria-hidden>
          $
        </span>
        <input
          aria-label={label}
          inputMode="decimal"
          value={value}
          placeholder="0.00"
          onChange={(e) => onChange(e.currentTarget.value)}
          aria-invalid={invalid || undefined}
          className={cn(
            'tabular h-7 w-20 rounded-md border border-input bg-background px-1.5 text-right font-mono text-xs focus-visible:outline-2 focus-visible:outline-ring',
            invalid && 'border-destructive',
          )}
        />
      </span>
      <span aria-live="polite" className="flex flex-wrap items-center justify-end gap-1 text-2xs" data-testid="what-if-result">
        {invalid ? <span className="text-destructive">Not a price</span> : null}
        {w ? (
          w.margin === null ? (
            <span className="text-muted-foreground">Margin unknown</span>
          ) : (
            <span data-band={w.band} className={cn('tabular rounded-sm px-1 font-mono leading-4', MARGIN_TONE[w.band])}>
              {pct1(w.margin)}
            </span>
          )
        ) : null}
        {w && w.monthly !== null ? <span className="tabular font-mono text-muted-foreground">{signed(w.monthly, cad)}/mo</span> : null}
      </span>
      {w?.belowCost ? <span className="text-2xs font-medium text-destructive">Below cost</span> : null}
    </div>
  )
}

export const PROPOSAL_STATE_LABEL: Record<ProposalState, string> = {
  open: 'To decide',
  approved: 'Approved',
  overridden: 'Overridden',
  skipped: 'Skipped',
  superseded: 'Superseded',
}

const STATE_TONE: Record<ProposalState, 'warning' | 'success' | 'primary' | 'neutral'> = {
  open: 'warning',
  approved: 'success',
  overridden: 'primary',
  skipped: 'neutral',
  superseded: 'neutral',
}

// --- summary -----------------------------------------------------------------------------------------------------------

const bandUnchanged = (p: PriceProposalOut): MarginBand => bandOf(p.band_unchanged, p.margin_pct_unchanged)

/** The alert's figures for the shown month. Superseded rows are history and are not counted. */
export function priceSummary(rows: PriceProposalOut[]) {
  const live = rows.filter((p) => p.state !== 'superseded')
  let seen = false
  let costChange = 0
  for (const p of live) {
    const a = num(p.cost_before)
    const b = num(p.cost_after)
    const n = num(p.quantity)
    if (a !== null && b !== null && n !== null) {
      seen = true
      costChange += (b - a) * n
    }
  }
  return {
    lines: live.length,
    costChange: seen ? costChange : null,
    negative: live.filter((p) => bandUnchanged(p) === 'negative').length,
    thin: live.filter((p) => ['under_5', 'under_10'].includes(bandUnchanged(p))).length,
    open: live.filter((p) => p.state === 'open').length,
  }
}

const ORDER: Record<ProposalState, number> = { open: 0, approved: 1, overridden: 1, skipped: 2, superseded: 3 }

export const monthLabel = (key: string): string => {
  const parts = formatWhen(`${key.slice(0, 7)}-01T12:00:00`, { style: 'date' }).split(' ')
  return `${parts[0] ?? key} ${key.slice(0, 4)}`
}

const ordinal = (n: number) =>
  `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`

const lineLabel = (p: PriceProposalOut) => `${p.company_name ?? 'Unknown company'} · ${p.product_name ?? 'licence'}`

/** "Books updated" vs "Sent to billing: <why>". */
export function booksOutcome(p: Pick<PriceProposalOut, 'billing_state' | 'billing_detail'>): string {
  if (p.billing_detail) return `Sent to billing: ${p.billing_detail}${p.billing_state === 'done' ? ' (billing marked it done)' : ''}`
  switch (p.billing_state) {
    case 'done':
      return 'Books updated'
    case 'auto_closed':
      return 'Books already had this price'
    case 'pending':
    case 'notified':
      return 'Sent to billing'
    default:
      return 'Books outcome not recorded yet'
  }
}

// --- decision cell -----------------------------------------------------------------------------------------------------

function NoticeDraft({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false)
  return (
    <details className="mt-1 max-w-80 rounded-md border border-border bg-card text-left" data-testid="notice-draft">
      <summary className="cursor-pointer px-2 py-1 text-xs font-medium">Customer notice draft · not sent</summary>
      <div className="space-y-1.5 border-t border-border px-2 py-1.5">
        <pre className="max-h-48 overflow-auto font-sans text-xs whitespace-pre-wrap">{text}</pre>
        <p className="text-2xs text-muted-foreground">Not sent. Review it and send it to the customer yourself.</p>
        <Button
          size="sm"
          onClick={() => {
            const clipboard = navigator.clipboard
            if (!clipboard?.writeText) {
              toast.error('Copy is not available here: select the text instead.')
              return
            }
            void clipboard.writeText(text).then(
              () => setCopied(true),
              () => toast.error('Could not copy: select the text instead.'),
            )
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </details>
  )
}

function Decision({
  p,
  booksWrite,
  whatIfPrice,
  busy,
  onApprove,
  onOverride,
  onSkip,
}: {
  p: PriceProposalOut
  booksWrite: boolean
  whatIfPrice: number | null
  busy: boolean
  onApprove: () => void
  onOverride: () => void
  onSkip: () => void
}) {
  if (p.state === 'open') {
    const proposed = num(p.proposed_sell)
    const canOverride = whatIfPrice !== null && !same(whatIfPrice, proposed)
    return (
      <div className="flex flex-wrap items-center justify-end gap-1.5 font-sans">
        <Button
          size="sm"
          variant="primary"
          disabled={busy || proposed === null}
          disabledReason={busy ? 'Saving…' : 'No proposed price: type one and Override'}
          onClick={onApprove}
        >
          {booksWrite ? 'Approve' : 'Approve, hand to billing'}
        </Button>
        <Button
          size="sm"
          disabled={busy || !canOverride}
          disabledReason={busy ? 'Saving…' : 'Type a different price in What if to override'}
          onClick={onOverride}
        >
          Override
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} disabledReason="Saving…" onClick={onSkip}>
          Skip
        </Button>
      </div>
    )
  }
  return (
    <div className="space-y-0.5 text-left font-sans text-xs" data-testid="proposal-decided">
      <span className="flex flex-wrap items-center gap-1.5">
        <Chip tone={STATE_TONE[p.state]} size="sm">
          {PROPOSAL_STATE_LABEL[p.state]}
        </Chip>
        {p.chosen_sell != null ? <span className="tabular font-mono">{cad(p.chosen_sell)}</span> : null}
      </span>
      {p.decided_at || p.decided_by ? (
        <span className="block text-muted-foreground">
          {p.decided_by?.name ?? 'Someone'}
          {p.decided_at ? ` · ${timeLabel(p.decided_at)}` : ''}
        </span>
      ) : null}
      {p.state === 'superseded' ? <span className="block text-muted-foreground">Recomputed: see the newer row</span> : null}
      {p.skip_reason ? <span className="block break-words">Reason: {p.skip_reason}</span> : null}
      {p.state === 'approved' || p.state === 'overridden' ? (
        <span className={cn('block break-words', p.billing_detail ? 'text-warning' : 'text-success')} data-testid="books-outcome">
          {booksOutcome(p)}
        </span>
      ) : null}
      {p.notice_draft ? <NoticeDraft text={p.notice_draft} /> : null}
    </div>
  )
}

// --- confirm dialogs ---------------------------------------------------------------------------------------------------

type Pending = { p: PriceProposalOut; rate: number | null }

function ConfirmPrice({
  pending,
  booksWrite,
  busy,
  onCancel,
  onConfirm,
}: {
  pending: Pending | null
  booksWrite: boolean
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const p = pending?.p
  const next = pending ? (pending.rate ?? num(pending.p.proposed_sell)) : null
  const old = num(p?.sell_before)
  const n = num(p?.quantity)
  const cost = num(p?.cost_after)
  const margin = marginPct(next, cost)
  const band = marginBand(margin)
  const monthly = next === null || old === null || n === null ? null : (next - old) * n
  const override = pending?.rate != null
  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
      title={override ? 'Override the price update' : 'Approve the price update'}
      description={p ? lineLabel(p) : undefined}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" pending={busy} disabled={busy} disabledReason="Saving…" onClick={onConfirm}>
            {booksWrite ? 'Confirm: update Books' : 'Confirm: hand to billing'}
          </Button>
        </div>
      }
    >
      {p ? (
        <div className="space-y-3 text-sm" data-testid="price-confirm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-muted-foreground">Price per user a month</dt>
            <dd className="tabular font-mono" data-testid="confirm-price">
              {old === null ? 'not billed now' : cad(old)} → {cad(next)}
            </dd>
            <dt className="text-muted-foreground">Each month</dt>
            <dd className="tabular font-mono" data-testid="confirm-monthly">
              {monthly === null ? 'unknown' : `${signed(monthly, cad)} on ${qty(p.quantity)} user${n === 1 ? '' : 's'}`}
            </dd>
            <dt className="text-muted-foreground">Margin at the new cost</dt>
            <dd data-testid="confirm-margin">
              {margin === null ? (
                <span className="text-muted-foreground">unknown (cost not known)</span>
              ) : (
                <span className={cn('tabular rounded-sm px-1.5 py-px font-mono text-xs', MARGIN_TONE[band])}>{pct1(margin)}</span>
              )}
              {cost !== null ? <span className="ml-2 text-xs text-muted-foreground">cost {cad(cost)}</span> : null}
            </dd>
            <dt className="text-muted-foreground">Takes effect</dt>
            <dd>{dayLabel(p.effective_date)}</dd>
          </dl>
          {next !== null && cost !== null && next < cost ? (
            <ErrorBanner
              title="Below cost"
              detail={`${cad(next)} is under the ${cad(cost)} cost after the change, so this line loses money every month. It is allowed; make sure that is meant.`}
            />
          ) : null}
          <p className="text-xs text-muted-foreground">
            {booksWrite
              ? 'Patchbay writes this rate on the Books recurring invoice and reads it back. If it cannot, billing gets the change to make by hand.'
              : 'Books writes are off: billing gets the change to make by hand.'}{' '}
            A customer notice is drafted for you to review. Nothing is sent to the customer.
          </p>
        </div>
      ) : null}
    </Dialog>
  )
}

function SkipDialog({
  p,
  busy,
  onCancel,
  onSkip,
}: {
  p: PriceProposalOut | null
  busy: boolean
  onCancel: () => void
  onSkip: (reason: string) => void
}) {
  const [reason, setReason] = React.useState('')
  React.useEffect(() => {
    if (p) setReason('')
  }, [p])
  const ok = reason.trim().length >= 3 && reason.trim().length <= 500
  return (
    <Dialog
      open={p !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
      title="No price change this time"
      description={p ? lineLabel(p) : undefined}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            pending={busy}
            disabled={busy || !ok}
            disabledReason={busy ? 'Saving…' : 'Give a reason (at least 3 characters)'}
            onClick={() => onSkip(reason.trim())}
          >
            Skip this update
          </Button>
        </div>
      }
    >
      <label className="grid gap-1 text-sm">
        <span>Why (kept with the decision)</span>
        <Textarea aria-label="Why no change" value={reason} onChange={(e) => setReason(e.target.value)} className="min-h-20" />
      </label>
    </Dialog>
  )
}

// --- the panel ---------------------------------------------------------------------------------------------------------

/** A request from the Renewals list to show one proposal. `month` is the line's likely month (`YYYY-MM`). */
export interface ProposalFocus {
  id: string
  month: string | null
  nonce: number
}

function Fig({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular text-right font-mono">{children}</dd>
    </>
  )
}

export function PriceUpdatesPanel({ focus }: { focus?: ProposalFocus | null }) {
  const [month, setMonth] = React.useState<string | null>(null)
  const q = useQuery({ ...priceUpdatesQuery(month), placeholderData: keepPreviousData })
  const queryClient = useQueryClient()
  const narrow = useIsSinglePane()
  const [edits, setEdits] = React.useState<Record<string, string>>({})
  const [confirm, setConfirm] = React.useState<Pending | null>(null)
  const [skipping, setSkipping] = React.useState<PriceProposalOut | null>(null)
  const [stale, setStale] = React.useState<string | null>(null)
  const [flash, setFlash] = React.useState<string | null>(null)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['licences'] })
  const failed = (error: unknown, what: string, p: PriceProposalOut) => {
    setConfirm(null)
    setSkipping(null)
    if (error instanceof ApiError && error.status === 409) {
      setStale(
        error.code === 'stale'
          ? `The numbers for ${lineLabel(p)} changed after you opened them, so nothing was written. Check the new figures and decide again.`
          : `${lineLabel(p)}: ${describeError(error)}. The list has been read again.`,
      )
      setEdits(({ [p.id]: _drop, ...rest }) => rest)
      void refresh()
      return
    }
    toast.error(`${what}: ${describeError(error)}`)
  }
  const approve = useMutation({
    mutationFn: ({ p, rate }: Pending) =>
      unwrap<PriceProposalOut>(
        approveLicencePriceApiV1LicencesPriceUpdatesProposalIdApprovePost({
          path: { proposal_id: p.id },
          body: { rate: rate === null ? null : rate.toFixed(2), computed_at: p.computed_at },
        }),
      ),
    onSuccess: async (out) => {
      setConfirm(null)
      setStale(null)
      toast.success(
        `${PROPOSAL_STATE_LABEL[out.state]} at ${cad(out.chosen_sell)}. ${booksOutcome(out)}. The customer notice is drafted, not sent.`,
      )
      await refresh()
    },
    onError: (error, vars) => failed(error, 'Not approved', vars.p),
  })
  const skip = useMutation({
    mutationFn: ({ p, reason }: { p: PriceProposalOut; reason: string }) =>
      unwrap<PriceProposalOut>(
        skipLicencePriceApiV1LicencesPriceUpdatesProposalIdSkipPost({ path: { proposal_id: p.id }, body: { reason } }),
      ),
    onSuccess: async () => {
      setSkipping(null)
      setStale(null)
      toast.success('Skipped: no price change this time.')
      await refresh()
    },
    onError: (error, vars) => failed(error, 'Not skipped', vars.p),
  })
  const busy = approve.isPending || skip.isPending

  // A Renewals row asked for its proposal: show its month, then scroll to it.
  const data = q.data
  React.useEffect(() => {
    if (!focus || !data) return
    if ((data.proposals ?? []).some((p) => p.id === focus.id)) {
      setFlash(focus.id)
      requestAnimationFrame(() =>
        document.querySelector(`[data-proposal="${CSS.escape(focus.id)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      )
      return
    }
    if (focus.month && focus.month !== data.month.slice(0, 7) && (data.months ?? []).some((m) => m.slice(0, 7) === focus.month))
      setMonth(focus.month)
  }, [focus, data])

  const textOf = (p: PriceProposalOut) => edits[p.id] ?? priceText(p.proposed_sell)
  const setValue = (p: PriceProposalOut, v: string) => setEdits((prev) => ({ ...prev, [p.id]: v }))
  const whatIfField = (p: PriceProposalOut) => (
    <WhatIfField
      label={`What if: price per user for ${lineLabel(p)}`}
      value={textOf(p)}
      onChange={(v) => setValue(p, v)}
      cost={p.cost_after}
      sellNow={p.sell_before}
      quantity={p.quantity}
    />
  )

  return (
    <section aria-label="Price updates" className="space-y-3 rounded-lg border border-border bg-card p-3" data-testid="price-updates">
      <QueryView query={q} what="Price updates">
        {(d) => {
          const booksWrite = d.books_write_enabled
          const shownMonth = d.month.slice(0, 7)
          const months = [...new Set([...(d.months ?? []).map((m) => m.slice(0, 7)), shownMonth])].sort()
          const rows = [...(d.proposals ?? [])].sort(
            (a, b) => ORDER[a.state] - ORDER[b.state] || (a.company_name ?? '').localeCompare(b.company_name ?? ''),
          )
          const s = priceSummary(d.proposals ?? [])
          const decision = (p: PriceProposalOut) => (
            <Decision
              p={p}
              booksWrite={booksWrite}
              whatIfPrice={parsePrice(textOf(p))}
              busy={busy}
              onApprove={() => setConfirm({ p, rate: null })}
              onOverride={() => {
                const rate = parsePrice(textOf(p))
                if (rate !== null) setConfirm({ p, rate })
              }}
              onSkip={() => setSkipping(p)}
            />
          )
          return (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="text-sm font-semibold">Price updates · {monthLabel(shownMonth)}</h2>
                <p className="text-xs text-muted-foreground">
                  {d.computed_at ? `Worked out ${timeLabel(d.computed_at)}` : 'Not worked out yet'} · the alert goes out on the{' '}
                  {ordinal(d.alert_day)} ·{' '}
                  {booksWrite ? 'Approve writes Books' : 'Books writes are off: Approve hands the change to billing'}
                </p>
              </div>
              {months.length > 1 ? (
                <FilterChips
                  label="Price update month"
                  value={shownMonth}
                  onChange={(m) => setMonth(m)}
                  options={months.map((m) => ({ id: m, label: monthLabel(m) }))}
                />
              ) : null}
              {stale ? <ErrorBanner tone="warning" title="The numbers changed" detail={stale} className="max-w-3xl" /> : null}
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5" data-testid="price-summary">
                <Tile label="Lines changing price" value={s.lines} />
                <Tile
                  label="Monthly cost change"
                  value={s.costChange === null ? '—' : `${signed(s.costChange, (v) => cadCompact(v))}/mo`}
                  tone={(s.costChange ?? 0) > 0.005 ? 'warning' : undefined}
                />
                <Tile label="Negative if unchanged" value={s.negative} tone={s.negative ? 'danger' : undefined} />
                <Tile label="Under 10% if unchanged" value={s.thin} tone={s.thin ? 'warning' : undefined} />
                <Tile label="To decide" value={s.open} tone={s.open ? 'warning' : undefined} />
              </div>
              {!d.computed_at ? (
                <EmptyState
                  title="Price updates have not been worked out yet."
                  detail="They are worked out after each nightly Pax8 sync."
                />
              ) : rows.length === 0 ? (
                <EmptyState title={`No licence prices change in ${monthLabel(shownMonth)}.`} />
              ) : narrow ? (
                <ul className="min-w-0 space-y-2.5" aria-label="Price updates list">
                  {rows.map((p) => (
                    <li
                      key={p.id}
                      data-proposal={p.id}
                      className={cn(
                        'min-w-0 rounded-lg border border-border p-3',
                        flash === p.id && 'bg-primary-soft',
                        p.state === 'superseded' && 'opacity-60',
                      )}
                    >
                      <p className="break-words text-sm font-medium">{lineLabel(p)}</p>
                      <p className="text-xs text-muted-foreground">
                        {qty(p.quantity)} users · {p.reason === 'renewal' ? 'renews' : 'new monthly rate'} {dayLabel(p.effective_date)}
                      </p>
                      <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                        <dl className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5">
                          <dt className="col-span-2 font-semibold text-primary">Now, per user</dt>
                          <Fig label="Cost">{cad(p.cost_before)}</Fig>
                          <Fig label="MSRP">{cad(p.msrp_before)}</Fig>
                          <Fig label="Sell">{cad(p.sell_before)}</Fig>
                          <Fig label="Margin">
                            <MarginValue when="Now" billed={p.sell_before} cost={p.cost_before} margin={p.margin_pct_before} />
                          </Fig>
                        </dl>
                        <dl className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5">
                          <dt className="col-span-2 font-semibold text-primary">After, per user</dt>
                          <Fig label="Cost">
                            <MoneyWithDelta value={p.cost_after} before={p.cost_before} tone="cost" />
                          </Fig>
                          <Fig label="MSRP">
                            <MoneyWithDelta value={p.msrp_after} before={p.msrp_before} />
                          </Fig>
                          <Fig label="Proposed">
                            <MoneyWithDelta value={p.proposed_sell} before={p.sell_before} />
                          </Fig>
                          <Fig label="Margin">
                            <MarginValue
                              when="At the proposed price"
                              billed={p.proposed_sell}
                              cost={p.cost_after}
                              margin={p.margin_pct_proposed}
                            />
                          </Fig>
                          <Fig label="If unchanged">
                            <MarginValue
                              when="If the price stays"
                              billed={p.sell_before}
                              cost={p.cost_after}
                              margin={p.margin_pct_unchanged}
                              band={p.band_unchanged}
                            />
                          </Fig>
                        </dl>
                      </div>
                      {p.state === 'open' ? <div className="mt-2 flex justify-end">{whatIfField(p)}</div> : null}
                      <div className="mt-2">{decision(p)}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="min-w-0 max-w-full">
                  <GroupedTable
                    label="Price updates"
                    columns={proposalColumns({ whatIf: whatIfField, decision })}
                    rows={rows}
                    rowKey={(p) => p.id}
                    rowProps={(p) => ({
                      'data-proposal': p.id,
                      className: cn(flash === p.id && 'bg-primary-soft', p.state === 'superseded' && 'opacity-60'),
                    })}
                    empty={<EmptyState title="No price updates." />}
                  />
                </div>
              )}
              <ConfirmPrice
                pending={confirm}
                booksWrite={booksWrite}
                busy={approve.isPending}
                onCancel={() => setConfirm(null)}
                onConfirm={() => confirm && approve.mutate(confirm)}
              />
              <SkipDialog
                p={skipping}
                busy={skip.isPending}
                onCancel={() => setSkipping(null)}
                onSkip={(reason) => skipping && skip.mutate({ p: skipping, reason })}
              />
            </>
          )
        }}
      </QueryView>
    </section>
  )
}

function proposalColumns({
  whatIf: whatIfCell,
  decision,
}: {
  whatIf: (p: PriceProposalOut) => React.ReactNode
  decision: (p: PriceProposalOut) => React.ReactNode
}): GroupedColumn<PriceProposalOut>[] {
  return [
    {
      id: 'line',
      header: 'Customer · licence',
      group: '',
      sortValue: (p) => p.company_name ?? '',
      cell: (p) => (
        <div className="min-w-44">
          <span className="font-medium">{p.company_name ?? 'Unknown company'}</span>
          <span className="block text-xs">{p.product_name ?? 'licence'}</span>
          <span className="block text-xs text-muted-foreground">
            {p.reason === 'renewal' ? 'Renews' : 'New monthly rate'} {dayLabel(p.effective_date)}
          </span>
        </div>
      ),
    },
    { id: 'users', header: 'Users', group: '', numeric: true, sortValue: (p) => num(p.quantity), cell: (p) => qty(p.quantity) },
    { id: 'cost_before', header: 'Cost', group: 'Now · per user', numeric: true, cell: (p) => cad(p.cost_before) },
    { id: 'msrp_before', header: 'MSRP', group: 'Now · per user', numeric: true, narrow: true, cell: (p) => cad(p.msrp_before) },
    { id: 'sell_before', header: 'Sell', group: 'Now · per user', numeric: true, cell: (p) => cad(p.sell_before) },
    {
      id: 'margin_before',
      header: 'Margin',
      group: 'Now · per user',
      numeric: true,
      sortValue: (p) => num(p.margin_pct_before),
      cell: (p) => (
        <MarginValue when="Now" billed={p.sell_before} cost={p.cost_before} margin={p.margin_pct_before} unit="per user a month" />
      ),
    },
    {
      id: 'cost_after',
      header: 'Cost',
      group: 'After · per user',
      numeric: true,
      cell: (p) => <MoneyWithDelta value={p.cost_after} before={p.cost_before} tone="cost" />,
    },
    {
      id: 'msrp_after',
      header: 'MSRP',
      group: 'After · per user',
      numeric: true,
      narrow: true,
      cell: (p) => <MoneyWithDelta value={p.msrp_after} before={p.msrp_before} />,
    },
    {
      id: 'proposed',
      header: 'Proposed',
      group: 'After · per user',
      numeric: true,
      cell: (p) => <MoneyWithDelta value={p.proposed_sell} before={p.sell_before} />,
    },
    {
      id: 'margin_proposed',
      header: 'Margin',
      group: 'After · per user',
      numeric: true,
      sortValue: (p) => num(p.margin_pct_proposed),
      cell: (p) => (
        <MarginValue
          when="At the proposed price"
          billed={p.proposed_sell}
          cost={p.cost_after}
          margin={p.margin_pct_proposed}
          seats={p.quantity}
          unit="per user a month"
        />
      ),
    },
    {
      id: 'margin_unchanged',
      header: 'If unchanged',
      group: 'After · per user',
      numeric: true,
      narrow: true,
      sortValue: (p) => num(p.margin_pct_unchanged),
      cell: (p) => (
        <MarginValue
          when="If the price stays"
          billed={p.sell_before}
          cost={p.cost_after}
          margin={p.margin_pct_unchanged}
          band={p.band_unchanged}
          seats={p.quantity}
          unit="per user a month"
        />
      ),
    },
    { id: 'what_if', header: 'What if', group: ' ', numeric: true, cell: (p) => (p.state === 'open' ? whatIfCell(p) : DASH_CELL) },
    { id: 'decision', header: 'Decision', group: ' ', cell: decision },
  ]
}
